import { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Stack,
  Alert,
  CircularProgress,
  Divider,
} from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import { QRCodeSVG } from "qrcode.react";
import { useLocation, useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { BizboxLogo } from "../../../common/components/bizbox-logo";
import { useMfaSetupStart, useMfaSetupVerify } from "../api/hooks";

const errMsg = (e: any) =>
  typeof e?.response?.data === "string"
    ? e.response.data
    : e?.response?.data?.message || e?.message || "Something went wrong.";

// Mandatory authenticator setup — reached when the admin requires MFA but the user hasn't enrolled.
// Full-page, no skip. feature/totp-mfa.
const MfaSetupPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { mfaToken, remember } = (location.state as { mfaToken?: string; remember?: boolean }) || {};

  const [uri, setUri] = useState("");
  const [manualKey, setManualKey] = useState("");
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);

  const startM = useMfaSetupStart();
  const verifyM = useMfaSetupVerify();

  useEffect(() => {
    // No password-bound setup ticket → cannot set up; send back to login.
    if (!mfaToken) {
      navigate("/login", { replace: true });
      return;
    }
    startM.mutate(
      { mfaToken },
      {
        onSuccess: (d) => {
          setUri(d.otpAuthUri);
          setManualKey(d.manualEntryKey);
        },
        onError: (e: any) => enqueueSnackbar(errMsg(e), { variant: "error" }),
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verify = () => {
    if (code.length !== 6 || !mfaToken) return;
    verifyM.mutate(
      { mfaToken, code, remember: !!remember },
      {
        onSuccess: (d) => {
          setBackupCodes(d.recoveryCodes);
          enqueueSnackbar("Authenticator enabled.", { variant: "success" });
        },
        onError: (e: any) => enqueueSnackbar(errMsg(e), { variant: "error" }),
      },
    );
  };

  // After setup, the Beacon session cookie is set. If we came from a product SSO login, resume it
  // (the product's validate-product-session re-check now succeeds); otherwise go home.
  const finish = () => {
    const params = new URLSearchParams(location.search);
    const callback = params.get("callback");
    const aud = params.get("aud");
    const origin = params.get("origin");
    if (callback) {
      window.location.href =
        `${callback}?tokenType=&expiresIn=&expirationDate=` +
        `&rememberMe=${!!remember}&origin=${origin ?? ""}&aud=${aud ?? ""}`;
    } else {
      navigate("/home", { replace: true });
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100vw",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "background.default",
        p: 2,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 440,
          background: "#fff",
          border: "1px solid #E2E7EF",
          borderRadius: "16px",
          boxShadow: 2,
          p: 4,
        }}
      >
        <Stack alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Box sx={{ width: 44, height: 44 }}>
            <BizboxLogo />
          </Box>
          <Typography variant="h1" fontSize={22} fontWeight={600} color="primary.main" align="center">
            Set up two-step verification
          </Typography>
          <Typography fontSize={13} color="text.secondary" align="center">
            Your administrator requires an authenticator app for your account. Complete setup to
            continue.
          </Typography>
        </Stack>

        {backupCodes ? (
          <Stack spacing={2}>
            <Alert severity="success" icon={<CheckCircle />}>
              Authenticator enabled.
            </Alert>
            <Typography fontSize={13} color="text.secondary">
              Save these backup codes. Each works once if you lose your device.
            </Typography>
            <Box
              data-testid="mfa-setup-backup-codes"
              sx={{
                fontFamily: "Consolas, monospace",
                fontSize: 15,
                lineHeight: 2,
                columns: 2,
                background: "#F7F9FC",
                border: "1px solid #E2E7EF",
                borderRadius: 1,
                p: 1.5,
              }}
            >
              {backupCodes.map((c) => (
                <div key={c}>{c}</div>
              ))}
            </Box>
            <Button
              variant="contained"
              onClick={finish}
              sx={{ textTransform: "none", borderRadius: "10px", py: 1.2 }}
              data-testid="mfa-setup-continue"
            >
              Continue
            </Button>
          </Stack>
        ) : startM.isPending || !uri ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2} alignItems="center">
            <Typography fontSize={13} color="text.secondary" alignSelf="flex-start">
              1. Scan this QR in Microsoft Authenticator, Google Authenticator, or Authy:
            </Typography>
            <Box sx={{ p: 1.5, background: "#fff", border: "1px solid #E2E7EF", borderRadius: 2 }}>
              <QRCodeSVG value={uri} size={168} />
            </Box>
            <Box sx={{ width: "100%" }}>
              <Typography fontSize={12} color="text.secondary">
                Can't scan? Enter this key manually:
              </Typography>
              <Box
                data-testid="mfa-setup-manual-key"
                sx={{
                  fontFamily: "Consolas, monospace",
                  fontSize: 14,
                  letterSpacing: 1,
                  background: "#FFF8E6",
                  border: "1px solid #F0D98A",
                  borderRadius: 1,
                  px: 1.5,
                  py: 1,
                  mt: 0.5,
                  userSelect: "all",
                  wordBreak: "break-all",
                }}
              >
                {manualKey}
              </Box>
            </Box>
            <Divider flexItem />
            <TextField
              data-testid="mfa-setup-code"
              label="2. Enter the 6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputProps={{ inputMode: "numeric", maxLength: 6, style: { letterSpacing: 6 } }}
              fullWidth
              autoFocus
            />
            <Button
              variant="contained"
              fullWidth
              onClick={verify}
              disabled={verifyM.isPending || code.length !== 6}
              sx={{ textTransform: "none", borderRadius: "10px", py: 1.2 }}
              data-testid="mfa-setup-verify"
            >
              {verifyM.isPending ? (
                <CircularProgress size={18} sx={{ color: "#fff" }} />
              ) : (
                "Verify & continue"
              )}
            </Button>
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default MfaSetupPage;
